/**
 * shared/fees.ts — Single source of truth for all fee amounts (AUD cents).
 *
 * Imported by:
 *   - Frontend (via @shared/fees path alias) for display prices
 *   - process-payment Lambda (relative import) for server-side validation
 *   - renewal-reminder-lambda (relative import) for email fee display
 *
 * Fee changes: edit FEES once, redeploy — both display and validation update automatically.
 */

export const FEES = {
  membership_fee_adult:          4000,   // $40
  membership_fee_junior:         3000,   // $30
  membership_fee_family:         12000,  // $120
  membership_fee_sponsor_gold:   250000, // $2,500
  membership_fee_sponsor_silver: 100000, // $1,000
  membership_fee_sponsor_bronze: 50000,  // $500
  competition_fee_member:        1000,   // $10
  competition_fee_nonmember:     1500,   // $15
} as const;

export type FeeKey = keyof typeof FEES;

/**
 * Returns the fee in AUD cents for a given key, or undefined for unknown keys.
 * Unknown keys are rejected by the payment Lambda with INVALID_FEE_KEY.
 */
export function getFeeCents(feeKey: string): number | undefined {
  return (FEES as Record<string, number>)[feeKey];
}

/**
 * Maps a membership type string to its canonical fee key.
 * Legacy 'SINGLE' maps to adult.
 */
export function membershipTypeToFeeKey(membershipType: string): FeeKey | undefined {
  const map: Record<string, FeeKey> = {
    ADULT:          'membership_fee_adult',
    SINGLE:         'membership_fee_adult', // legacy
    JUNIOR:         'membership_fee_junior',
    FAMILY:         'membership_fee_family',
    SPONSOR_GOLD:   'membership_fee_sponsor_gold',
    SPONSOR_SILVER: 'membership_fee_sponsor_silver',
    SPONSOR_BRONZE: 'membership_fee_sponsor_bronze',
  };
  return map[membershipType?.toUpperCase()];
}

// ---------------------------------------------------------------------------
// Family price (#127, decisions D1, D2, D4, A1, A2)
// ---------------------------------------------------------------------------

/** A family has at most 2 adults (A1); a third adult takes an individual membership. */
export const FAMILY_MAX_ADULTS = 2;
/** Juniors covered by the $120 cap (D1). */
export const FAMILY_INCLUDED_JUNIORS = 3;
/** Largest family, people including the primary (D3). Admins may exceed it with a reason. */
export const FAMILY_MAX_SIZE = 10;
/** Each junior beyond 3 adds $25 (D2). */
export const FAMILY_EXTRA_JUNIOR_CENTS = 2500;
/** A family of up to 2 adults + 3 juniors never pays more than this (D1). */
export const FAMILY_CAP_CENTS = FEES.membership_fee_family;

export interface Household {
  adults: number;
  juniors: number;
}

/**
 * The family price in cents (D1, D2): the lower of the individual prices
 * added up or $120 for up to 2 adults and 3 juniors, plus $25 for each junior
 * beyond 3. Throws for more than 2 adults (A1) or a negative count.
 * Examples: 1+1 = $70, 2+0 = $80, 2+1 = $110, 2+2 = $120, 2+3 = $120,
 * 2+4 = $145, 2+8 = $245.
 */
export function familyPriceCents({ adults, juniors }: Household): number {
  if (!Number.isInteger(adults) || !Number.isInteger(juniors) || adults < 0 || juniors < 0) {
    throw new Error('familyPriceCents: adults and juniors must be whole numbers, 0 or more');
  }
  if (adults > FAMILY_MAX_ADULTS) {
    throw new Error(`familyPriceCents: a family has at most ${FAMILY_MAX_ADULTS} adults`);
  }
  const included = Math.min(juniors, FAMILY_INCLUDED_JUNIORS);
  const extra = Math.max(0, juniors - FAMILY_INCLUDED_JUNIORS);
  const base = Math.min(
    adults * FEES.membership_fee_adult + included * FEES.membership_fee_junior,
    FAMILY_CAP_CENTS,
  );
  return base + extra * FAMILY_EXTRA_JUNIOR_CENTS;
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * How the price was worked out (R2.3), e.g. "1 adult + 1 junior = $70",
 * "2 adults + 3 juniors = $120 (capped)", "2 adults + 5 juniors = $120 + 2 extra juniors × $25 = $170".
 */
export function describeFamilyPrice({ adults, juniors }: Household): string {
  const total = familyPriceCents({ adults, juniors });
  const who = [adults ? plural(adults, 'adult', 'adults') : '', juniors ? plural(juniors, 'junior', 'juniors') : '']
    .filter(Boolean)
    .join(' + ') || 'nobody';
  const extra = Math.max(0, juniors - FAMILY_INCLUDED_JUNIORS);
  if (extra > 0) {
    return `${who} = ${dollars(FAMILY_CAP_CENTS)} + ${plural(extra, 'extra junior', 'extra juniors')} × ${dollars(FAMILY_EXTRA_JUNIOR_CENTS)} = ${dollars(total)}`;
  }
  const sum = adults * FEES.membership_fee_adult + juniors * FEES.membership_fee_junior;
  return sum > FAMILY_CAP_CENTS ? `${who} = ${dollars(total)} (capped)` : `${who} = ${dollars(total)}`;
}

/** The increase in family price when the family changes (A2); never negative. */
export function familyChangeIncreaseCents(before: Household, after: Household): number {
  return Math.max(0, familyPriceCents(after) - familyPriceCents(before));
}
